import { Alert, AnimatedContentLoader, BetaPill, Button } from '@root/components'
import { useBusinessContext } from '@root/context'
import { checkElsterCertificate } from '@root/query/integrations/elster'
import {
  IVatPeriod,
  invalidateDashboardQueries,
  reportVatPeriod,
  reportVatPeriodToElster,
  ElsterVatReportResponse
} from '@root/query'
import { useBusinessIdentifiers } from '@root/query/hooks'
import { getSettingsPageUrl } from '@root/constants'
import { formatDate } from '@root/utils'
import React from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaCheckCircle, FaPaperPlane } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

interface Props {
  vatPeriod: IVatPeriod
  onClose: () => void
}

type ReportMode = 'elster' | 'manual'

const hasIdentifier = (identifiers: { type: string; value: string }[], type: string): boolean =>
  identifiers.some(identifier => identifier.type === type && Boolean(identifier.value))

const getElsterReportToastError = (
  error: unknown,
  messages: { filingInProgress: string; upstreamUnavailable: string; fallback: string }
): string => {
  const { status, data } =
    (error as { status?: number; data?: { message?: string } } | undefined) ?? {}

  if (status === 409) {
    return data?.message || messages.filingInProgress
  }
  if (status === 502) {
    return data?.message || messages.upstreamUnavailable
  }
  if (status === 400 && data?.message) {
    return data.message
  }
  return messages.fallback
}

const ElsterReportModal: React.FC<Props> = ({ vatPeriod, onClose }) => {
  const queryClient = useQueryClient()
  const { slug, data: business } = useBusinessContext()
  const [t] = useTranslation()
  const [mode, setMode] = React.useState<ReportMode>('elster')
  const [filingInProgress, setFilingInProgress] = React.useState(false)

  const {
    data: certStatus,
    isLoading: isCertLoading,
    isError: certError
  } = useQuery({
    queryKey: [slug, 'has_elster_certificate'],
    queryFn: () => checkElsterCertificate({ slug }),
    retry: 1
  })

  const {
    data: identifiers = [],
    isLoading: isIdentifiersLoading,
    isError: identifiersError
  } = useBusinessIdentifiers()

  const hasCertificate = Boolean(certStatus?.has_certificate)
  const hasSteuernummer = hasIdentifier(identifiers, 'steuernummer')
  const hasFinanzamtsnummer = Boolean(business?.finanzamtsnummer)
  const hasElsterAddress = Boolean(
    (business?.business_city || business?.invoicing_city) &&
      (business?.business_postal_code || business?.invoicing_postal_code)
  )
  const hasCompanyDetails = hasSteuernummer && hasElsterAddress
  const hasFinanzkasseIban = Boolean(business?.finanzkasse_iban)
  const hasFinanzkasseRecipient = Boolean(business?.finanzkasse_recipient_name)
  const canSendToElster =
    hasCertificate &&
    hasCompanyDetails &&
    hasFinanzamtsnummer &&
    hasFinanzkasseIban &&
    hasFinanzkasseRecipient
  const isReadinessLoading = isCertLoading || isIdentifiersLoading
  const readinessError = certError || identifiersError

  const reportToElsterMutation = useMutation({
    mutationFn: () => reportVatPeriodToElster({ slug, vatPeriodId: vatPeriod.id }),
    ...{
      onSuccess: async (data: ElsterVatReportResponse) => {
        queryClient.setQueryData<IVatPeriod>([slug, 'vat_periods', vatPeriod.id], current =>
          current
            ? {
                ...current,
                is_reported: true,
                filing_tax_to_be_paid: data?.tax_to_be_paid ?? current.filing_tax_to_be_paid,
                purchase_invoice_id: data?.purchase_invoice_id ?? current.purchase_invoice_id
              }
            : current
        )
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
        await queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods', vatPeriod.id] })
        await invalidateDashboardQueries(queryClient, slug)
        onClose()
      },
      onError: error => {
        const status = (error as { status?: number } | undefined)?.status
        if (status === 409) {
          setFilingInProgress(true)
        }
      }
    }
  })

  const reportMutation = useMutation({
    mutationFn: () => reportVatPeriod({ slug, vatPeriodId: vatPeriod.id }),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods', vatPeriod.id] })
        void invalidateDashboardQueries(queryClient, slug)
        toast.success(t('vatPage.reportSuccess'))
        onClose()
      },
      onError: () => {
        toast.error(t('error.oho'))
      }
    }
  })

  const isPending = reportMutation.isPending || reportToElsterMutation.isPending

  return (
    <StyledModalContainer>
      <Content>
        <PeriodBlock>
          <PeriodTitle>{t('vatPage.elsterReport.title')}</PeriodTitle>
          <PeriodRange>
            {t('vatPage.elsterReport.period')}{' '}
            <strong>
              {formatDate(vatPeriod.start_date)} – {formatDate(vatPeriod.end_date)}
            </strong>
          </PeriodRange>
        </PeriodBlock>

        <InfoText>{t('vatPage.elsterReport.info')}</InfoText>

        <OptionList>
          <Option $selected={mode === 'elster'}>
            <input
              type="radio"
              name="elster-report-mode"
              value="elster"
              checked={mode === 'elster'}
              onChange={() => setMode('elster')}
            />
            <OptionContent>
              <OptionTitle>
                {t('vatPage.elsterReport.options.elster.title')}
                <BetaPill label={t('general.beta.label')} tooltip={t('general.beta.tooltip')} />
              </OptionTitle>
              <OptionDescription>
                {t('vatPage.elsterReport.options.elster.description')}
              </OptionDescription>
            </OptionContent>
          </Option>

          <Option $selected={mode === 'manual'}>
            <input
              type="radio"
              name="elster-report-mode"
              value="manual"
              checked={mode === 'manual'}
              onChange={() => setMode('manual')}
            />
            <OptionContent>
              <OptionTitle>{t('vatPage.elsterReport.options.manual.title')}</OptionTitle>
              <OptionDescription>
                {t('vatPage.elsterReport.options.manual.description')}
              </OptionDescription>
            </OptionContent>
          </Option>
        </OptionList>

        <AnimatedContentLoader isLoading={isReadinessLoading}>
          <Alerts>
            {readinessError && <Alert type="error" description={t('error.oho')} marginBottom={0} />}
            {!readinessError && !hasCompanyDetails && mode === 'elster' && (
              <Alert
                type="info"
                title={t('vatPage.elsterReport.missingBusinessData.title')}
                marginBottom={0}
                description={
                  <>
                    {t('vatPage.elsterReport.missingBusinessData.description')}{' '}
                    <Link to={getSettingsPageUrl(slug, { page: 'settings' })}>
                      {t('vatPage.elsterReport.openBusinessSettings')}
                    </Link>
                  </>
                }
              />
            )}
            {!readinessError && !hasFinanzamtsnummer && mode === 'elster' && (
              <Alert
                type="info"
                title={t('vatPage.elsterReport.missingFinanzamtsnummer.title')}
                marginBottom={0}
                description={
                  <>
                    {t('vatPage.elsterReport.missingFinanzamtsnummer.description')}{' '}
                    <Link to={getSettingsPageUrl(slug, { page: 'automation-vat-settings' })}>
                      {t('vatPage.elsterReport.openVatSettings')}
                    </Link>
                  </>
                }
              />
            )}
            {!readinessError && !hasCertificate && mode === 'elster' && (
              <Alert
                type="info"
                title={t('vatPage.elsterReport.missingCertificate.title')}
                marginBottom={0}
                description={
                  <>
                    {t('vatPage.elsterReport.missingCertificate.description')}{' '}
                    <Link to={getSettingsPageUrl(slug, { page: 'elster' })}>
                      {t('vatPage.elsterReport.openSettings')}
                    </Link>
                  </>
                }
              />
            )}
            {!readinessError && !hasFinanzkasseIban && mode === 'elster' && (
              <Alert
                type="info"
                title={t('vatPage.elsterReport.missingFinanzkasseIban.title')}
                marginBottom={0}
                description={
                  <>
                    {t('vatPage.elsterReport.missingFinanzkasseIban.description')}{' '}
                    <Link to={getSettingsPageUrl(slug, { page: 'automation-vat-settings' })}>
                      {t('vatPage.elsterReport.openVatSettings')}
                    </Link>
                  </>
                }
              />
            )}
            {!readinessError && !hasFinanzkasseRecipient && mode === 'elster' && (
              <Alert
                type="info"
                title={t('vatPage.elsterReport.missingFinanzkasseRecipient.title')}
                marginBottom={0}
                description={
                  <>
                    {t('vatPage.elsterReport.missingFinanzkasseRecipient.description')}{' '}
                    <Link to={getSettingsPageUrl(slug, { page: 'automation-vat-settings' })}>
                      {t('vatPage.elsterReport.openVatSettings')}
                    </Link>
                  </>
                }
              />
            )}
          </Alerts>
        </AnimatedContentLoader>
      </Content>

      <StyledFooter>
        <Button isSecondary onClick={() => onClose()}>
          {t('general.close')}
        </Button>
        {mode === 'elster' ? (
          <Button
            icon={<FaPaperPlane />}
            intent="success"
            disabled={!canSendToElster || isPending || isReadinessLoading || filingInProgress}
            showSpinner={reportToElsterMutation.isPending}
            onClick={() => {
              const promise = reportToElsterMutation.mutateAsync()
              toast.promise(promise, {
                loading: t('vatPage.elsterReport.form.submitting'),
                success: t('vatPage.elsterReport.form.success'),
                error: error =>
                  getElsterReportToastError(error, {
                    filingInProgress: t('vatPage.elsterReport.errors.filingInProgress'),
                    upstreamUnavailable: t('vatPage.elsterReport.errors.upstreamUnavailable'),
                    fallback: t('error.oho')
                  })
              })
            }}
          >
            {t('vatPage.elsterReport.form.submit')}
          </Button>
        ) : (
          <Button
            intent="success"
            onClick={() => reportMutation.mutate()}
            icon={<FaCheckCircle />}
            disabled={isPending}
            showSpinner={reportMutation.isPending}
          >
            {t('vatPage.markReported')}
          </Button>
        )}
      </StyledFooter>
    </StyledModalContainer>
  )
}

export default ElsterReportModal

const StyledModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const PeriodBlock = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const PeriodTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.menuBg};
`

const PeriodRange = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`

const InfoText = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

const Option = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.neutralGray)};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  cursor: pointer;

  input {
    margin: 1px 0 0;
    width: 17px;
    height: 17px;
    accent-color: ${({ theme }) => theme.colors.primary};
    flex: 0 0 auto;
  }
`

const OptionContent = styled.span`
  flex: 1;
  min-width: 0;
`

const OptionTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const OptionDescription = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Alerts = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  height: fit-content;
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.md}rem;
`
